import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useSubscriptionStore } from "@/stores/subscriptionStore";
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { fetchPlansForUser } from "@/lib/subscription-plans";
import { AlertCircle, Users, FileUp, ArrowRight, Sparkles } from "lucide-react";
import { SubscriptionModal } from "@/components/payment/SubscriptionModal";
import { formatPrice } from "@/lib/formatters";

interface SubscriptionPlan {
  code: string;
  name: string;
  max_patients: number | null;
  max_file_uploads: number | null;
  price_cents: number;
  features?: unknown;
  [key: string]: unknown; // Para permitir outras propriedades do banco
}

interface UpgradeModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  reason: 'patients' | 'file_uploads';
  currentCount?: number;
  requestedCount?: number;
}

export function UpgradeModal({
  open,
  onOpenChange,
  reason,
  currentCount = 0,
  requestedCount = 0,
}: UpgradeModalProps) {
  const { currentPlan, getMaxPatients, getMaxFileUploads } = useSubscriptionStore();
  const [availablePlans, setAvailablePlans] = useState<SubscriptionPlan[]>([]);
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<SubscriptionPlan | null>(null);
  const [loading, setLoading] = useState(false);

  const loadAvailablePlans = async () => {
    try {
      setLoading(true);
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const plans = await fetchPlansForUser(user.id, {
        excludeFree: true,
        orderBy: 'price_cents',
      });

      // Filtrar planos que atendem ao requisito
      const suitablePlans = reason === 'patients'
        ? plans.filter(plan => {
            const maxPatients = plan.max_patients;
            return maxPatients === null || maxPatients > (getMaxPatients() || 0);
          })
        : plans.filter(plan => {
            const maxUploads = plan.max_file_uploads;
            return maxUploads !== 1 && (maxUploads === null || maxUploads >= requestedCount);
          });

      setAvailablePlans(suitablePlans);
    } catch (error) {
      console.error('Error loading plans:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      loadAvailablePlans();
      supabase.auth.getSession().then(({ data: { session } }) => {
        if (session?.access_token) {
          const url = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/trigger-suggest-subscription`;
          fetch(url, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${session.access_token}`,
            },
            body: JSON.stringify({ reason }),
          }).catch(() => {});
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleUpgrade = (plan: SubscriptionPlan) => {
    setSelectedPlan(plan);
    setShowSubscriptionModal(true);
  };

  const handleSubscriptionSuccess = async () => {
    setShowSubscriptionModal(false);
    onOpenChange(false);
    // Recarregar dados do plano
    await useSubscriptionStore.getState().loadSubscription();
    // Aguardar um pouco para garantir que o banco foi atualizado
    await new Promise(resolve => setTimeout(resolve, 500));
    // Recarregar apenas se necessário (evitar reload completo)
    window.location.reload();
  };

  const getReasonMessage = () => {
    if (reason === 'patients') {
      const maxPatients = getMaxPatients();
      const planName = currentPlan?.name || 'Free';
      return {
        title: 'Limite de Pacientes Atingido',
        description: `Você atingiu o limite de ${maxPatients || 'N/A'} pacientes do plano ${planName}.`,
        icon: Users,
      };
    } else {
      const maxUploads = getMaxFileUploads();
      return {
        title: 'Upload Múltiplo Não Disponível',
        description: `Seu plano atual (${currentPlan?.name || 'Plano Gratuito'}) ${maxUploads === 1 ? 'não permite upload múltiplo' : `permite até ${maxUploads} arquivos por vez`}. Você tentou fazer upload de ${requestedCount} arquivo(s).`,
        icon: FileUp,
      };
    }
  };

  const reasonInfo = getReasonMessage();
  const ReasonIcon = reasonInfo.icon;

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-amber-500" />
              {reasonInfo.title}
            </DialogTitle>
            <DialogDescription>
              {reasonInfo.description}
            </DialogDescription>
          </DialogHeader>

          {reason === 'patients' && (
            <Alert>
              <ReasonIcon className="h-4 w-4" />
              <AlertDescription>
                Assine um plano superior para adicionar mais pacientes.
              </AlertDescription>
            </Alert>
          )}
          {reason === 'file_uploads' && (
            <Alert>
              <ReasonIcon className="h-4 w-4" />
              <AlertDescription>
                Assine um plano superior para fazer upload de múltiplos arquivos simultaneamente.
              </AlertDescription>
            </Alert>
          )}

          {loading ? (
            <div className="py-8 text-center text-muted-foreground">
              Carregando planos disponíveis...
            </div>
          ) : availablePlans.length === 0 ? (
            <Alert>
              <AlertDescription>
                Não há planos disponíveis que atendam ao seu requisito no momento.
              </AlertDescription>
            </Alert>
          ) : (
            <div className="space-y-4">
              <h3 className="font-semibold">Planos Disponíveis</h3>
              <div className="grid gap-4 md:grid-cols-2">
                {availablePlans.map((plan) => (
                  <Card key={plan.code} className="relative">
                    <CardHeader>
                      <div className="flex items-center justify-between">
                        <CardTitle className="text-lg">{plan.name}</CardTitle>
                        {plan.code === 'premium' && (
                          <Sparkles className="h-5 w-5 text-yellow-500" />
                        )}
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="text-2xl font-bold">
                        {formatPrice(plan.price_cents)}
                        <span className="text-sm font-normal text-muted-foreground">/mês</span>
                      </div>

                      <div className="space-y-2 text-sm">
                        <div className="flex items-center gap-2">
                          <Users className="h-4 w-4 text-muted-foreground" />
                          <span>
                            {plan.max_patients === null 
                              ? 'Pacientes ilimitados' 
                              : `Até ${plan.max_patients} pacientes`}
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          <FileUp className="h-4 w-4 text-muted-foreground" />
                          <span>
                            {plan.max_file_uploads === null
                              ? 'Upload múltiplo ilimitado'
                              : plan.max_file_uploads === 1
                              ? 'Upload único'
                              : `Até ${plan.max_file_uploads} arquivos por vez`}
                          </span>
                        </div>
                      </div>

                      <Button
                        onClick={() => handleUpgrade(plan)}
                        className="w-full"
                        variant={plan.code === 'premium' ? 'default' : 'outline'}
                      >
                        Assinar Agora
                        <ArrowRight className="h-4 w-4 ml-2" />
                      </Button>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          )}

          <div className="flex justify-end gap-2 pt-4 border-t">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Fechar
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {selectedPlan && (
        <SubscriptionModal
          open={showSubscriptionModal}
          onOpenChange={setShowSubscriptionModal}
          planCode={selectedPlan.code}
          planName={selectedPlan.name}
          planPrice={selectedPlan.price_cents}
          onSuccess={handleSubscriptionSuccess}
        />
      )}
    </>
  );
}


